import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme, type Theme } from "@mui/material/styles";
import type { GridColDef, GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import { LojaDataGrid } from "../../../common";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import { referenciaExtratoResumida } from "../helpers/extrato-movimentacao-labels";
import type {
  MovimentacaoFinanceiraTipoVisual,
  MovimentacaoFinanceiraView,
} from "../types/extrato-financeiro.types";
import ExtratoMovimentacaoDetalheDialog from "./ExtratoMovimentacaoDetalheDialog";

type Row = MovimentacaoFinanceiraView;

function chipColor(
  tv: MovimentacaoFinanceiraTipoVisual
): "success" | "error" | "warning" | "default" {
  switch (tv) {
    case "entrada":
      return "success";
    case "saida":
      return "error";
    case "taxa":
      return "warning";
    default:
      return "default";
  }
}

function corValor(theme: Theme, tv: MovimentacaoFinanceiraTipoVisual) {
  switch (tv) {
    case "entrada":
      return theme.palette.success.main;
    case "saida":
    case "taxa":
      return theme.palette.error.main;
    default:
      return theme.palette.text.primary;
  }
}

type Props = {
  linhas: MovimentacaoFinanceiraView[];
  total: number;
  page: number;
  limit: number;
  loading?: boolean;
  onPaginationModelChange: (m: GridPaginationModel) => void;
};

export default function ExtratoFinanceiroTabela({
  linhas,
  total,
  page,
  limit,
  loading,
  onPaginationModelChange,
}: Props) {
  const theme = useTheme();
  const [detalhe, setDetalhe] = useState<MovimentacaoFinanceiraView | null>(null);

  const rows: Row[] = useMemo(() => linhas.map((r) => ({ ...r, id: r.id })), [linhas]);

  const columns: GridColDef<Row>[] = [
    {
      field: "dataMovimentacaoLabel",
      headerName: "Data",
      width: 142,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ py: 0.5 }}>
          {p.row.dataMovimentacaoLabel}
        </Typography>
      ),
    },
    {
      field: "contaFinanceiraNome",
      headerName: "Conta",
      width: 150,
      renderCell: (p) => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap title={p.row.contaFinanceiraNome}>
            {p.row.contaFinanceiraNome}
          </Typography>
          {p.row.contaDestinoNome ? (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              → {p.row.contaDestinoNome}
            </Typography>
          ) : null}
        </Box>
      ),
    },
    {
      field: "tipoLabel",
      headerName: "Tipo",
      width: 118,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.row.tipoLabel}
          color={chipColor(p.row.tipoVisual)}
          variant={p.row.tipoVisual === "entrada" ? "filled" : "outlined"}
          sx={{ my: 0.5 }}
        />
      ),
    },
    {
      field: "origemLabel",
      headerName: "Origem",
      width: 118,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ py: 0.5 }} noWrap title={p.row.origemLabel}>
          {p.row.origemLabel}
        </Typography>
      ),
    },
    {
      field: "descricaoPrincipal",
      headerName: "Descrição",
      flex: 1,
      minWidth: 220,
      renderCell: (p) => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={p.row.descricaoPrincipal}
          >
            {p.row.descricaoPrincipal}
          </Typography>
          {p.row.descricaoSubtitulo ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={p.row.descricaoSubtitulo}
            >
              {p.row.descricaoSubtitulo}
            </Typography>
          ) : null}
        </Box>
      ),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            py: 0.5,
            width: "100%",
            textAlign: "right",
            color: corValor(theme, p.row.tipoVisual),
          }}
        >
          {formatarMoedaBRL(p.row.valor)}
        </Typography>
      ),
    },
    {
      field: "refs",
      headerName: "Referência",
      width: 160,
      renderCell: (p) => (
        <Typography
          variant="caption"
          sx={{ py: 0.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          title={referenciaExtratoResumida(p.row)}
        >
          {referenciaExtratoResumida(p.row)}
        </Typography>
      ),
    },
  ];

  const handleRowClick = (params: GridRowParams<Row>) => {
    setDetalhe(params.row);
  };

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              Movimentações
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Clique na linha para ver detalhes
            </Typography>
          </Box>
          <Box sx={{ px: 2, pb: 2, width: "100%", overflowX: "auto" }}>
            <LojaDataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              paginationMode="server"
              rowCount={total}
              paginationModel={{
                page: Math.max(0, page - 1),
                pageSize: limit,
              }}
              onPaginationModelChange={onPaginationModelChange}
              onRowClick={handleRowClick}
              pageSizeOptions={[10, 20, 50, 100]}
              disableColumnMenu
              columnHeaderHeight={44}
              rowHeight={56}
              getRowClassName={(params) =>
                params.row.semCentro ? "extrato-row-sem-centro" : ""
              }
              sx={{
                minWidth: 1040,
                "& .MuiDataGrid-row": { cursor: "pointer" },
                "& .extrato-row-sem-centro": {
                  backgroundColor: alpha(theme.palette.warning.main, 0.06),
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <ExtratoMovimentacaoDetalheDialog
        movimentacao={detalhe}
        onClose={() => setDetalhe(null)}
      />
    </>
  );
}
